import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Fingerprint, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { STORAGE_KEYS } from '@/lib/config'
import { isPasskeySupported, listPasskeys, registerPasskey } from '@/api/passkeys'

const DISMISS_COOLDOWN_DAYS = 30
const SHOWN_SESSION_KEY = 'passkey_nudge_shown_this_session'

/**
 * Nudge post-login : propose à l'utilisateur d'enrôler un passkey s'il
 * n'en a pas encore. S'affiche au 1er chargement d'une route protégée
 * (dashboard, manager), masqué pendant 30 jours si "Plus tard".
 *
 * Le composant s'auto-décide : pas besoin de logique côté caller.
 */
export function PasskeyNudge() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!isPasskeySupported()) return
    if (sessionStorage.getItem(SHOWN_SESSION_KEY)) return

    const dismissedAt = localStorage.getItem(STORAGE_KEYS.PASSKEY_NUDGE_DISMISSED_AT)
    if (dismissedAt) {
      const elapsedMs = Date.now() - Number.parseInt(dismissedAt, 10)
      const cooldownMs = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
      if (Number.isFinite(elapsedMs) && elapsedMs < cooldownMs) return
    }

    let cancelled = false
    void listPasskeys()
      .then((passkeys) => {
        if (cancelled) return
        // Déjà configuré → on ne dérange pas
        if (passkeys.length > 0) return
        // Petite respiration pour ne pas concurrencer le 1er rendu du dashboard
        window.setTimeout(() => {
          if (cancelled) return
          setOpen(true)
          sessionStorage.setItem(SHOWN_SESSION_KEY, '1')
        }, 800)
      })
      .catch(() => {
        // API non joignable / token pas encore prêt → silencieux
      })

    return () => {
      cancelled = true
    }
  }, [])

  const registerMutation = useMutation({
    mutationFn: () => registerPasskey(deviceLabel()),
    onSuccess: () => {
      toast.success(t('passkeyNudge.success', "Clé d'accès activée."))
      void queryClient.invalidateQueries({ queryKey: ['passkeys'] })
      // Pas de dismissed_at : l'user a maintenant un passkey, le filtre
      // listPasskeys().length>0 évite tout re-prompt naturellement.
      setOpen(false)
    },
    onError: (error) => {
      if (
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'AbortError')
      ) {
        // Annulation prompt biométrique : on traite comme "Plus tard"
        markDismissed()
        setOpen(false)
        return
      }
      console.error('[PasskeyNudge] register failed', error)
      toast.error(t('passkeyNudge.error', "Activation impossible"))
    },
  })

  const markDismissed = () => {
    localStorage.setItem(STORAGE_KEYS.PASSKEY_NUDGE_DISMISSED_AT, String(Date.now()))
  }

  const handleLater = () => {
    markDismissed()
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Fermeture par overlay ou Esc → équivalent "Plus tard"
        if (!next) {
          if (!registerMutation.isPending) handleLater()
          return
        }
        setOpen(true)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center">
            <Fingerprint className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <DialogTitle className="text-center">
            {t('passkeyNudge.title', "Activer une clé d'accès ?")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t(
              'passkeyNudge.description',
              'Pour vous connecter en un geste, sans mot de passe.',
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleLater}
            disabled={registerMutation.isPending}
          >
            {t('passkeyNudge.later', 'Plus tard')}
          </Button>
          <Button
            type="button"
            onClick={() => registerMutation.mutate()}
            disabled={registerMutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {registerMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {t('passkeyNudge.activate', 'Activer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function deviceLabel(): string {
  const ua = navigator.userAgent
  if (/iPhone/i.test(ua)) return 'iPhone'
  if (/iPad/i.test(ua)) return 'iPad'
  if (/Mac/i.test(ua)) return 'Mac'
  if (/Android/i.test(ua)) return 'Android'
  if (/Windows/i.test(ua)) return 'Windows'
  return 'Passkey'
}
